import { useCallback, useEffect, useState } from 'react'
import { PrepareAPIService } from '../api/http-service/PrepareAPIService'

export interface EnrichmentStatus {
  id: string
  name: string
  key: string
  type: 'research' | 'prompt' | 'translate'
  provider?: string
  runInBackground: boolean
  status: 'ready' | 'running' | 'completed' | 'failed'
  lastRunAt?: Date
  currentJobId?: string
  recordsProcessed?: number
  totalRecords?: number
  error?: string
}

export interface EnrichmentStatusResponse {
  sheetId: string
  enrichments: EnrichmentStatus[]
  summary: {
    total: number
    ready: number
    running: number
    completed: number
    failed: number
    backgroundEnabled: number
  }
}

export function useEnrichmentStatus(sheetId?: string) {
  const apiClient = new PrepareAPIService().client
  const [status, setStatus] = useState<EnrichmentStatusResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch initial status
  const fetchStatus = async () => {
    if (!sheetId) return

    setLoading(true)
    setError(null)

    try {
      const response = await apiClient.enrichments.getStatus(sheetId)
      setStatus(response)
    } catch (err) {
      // Handle 404 gracefully - it means no enrichments are configured yet
      if (err instanceof Error && err.message.includes('404')) {
        setStatus({
          sheetId,
          enrichments: [],
          summary: {
            total: 0,
            ready: 0,
            running: 0,
            completed: 0,
            failed: 0,
            backgroundEnabled: 0,
          },
        })
      } else {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to fetch enrichment status'
        )
        console.error('Error fetching enrichment status:', err)
      }
    } finally {
      setLoading(false)
    }
  }

  // Handle enrichment events
  const handleEnrichmentEvent = useCallback(
    (event: any) => {
      if (!sheetId || !event.data) return

      if (
        event.event === 'enrichment:started' &&
        event.data.sheetId === sheetId
      ) {
        // Mark enrichments as started/running
        setStatus((prev) => {
          if (!prev) return prev

          const updated = { ...prev }
          const enrichmentKeys =
            event.data.enrichments?.map((e: any) => e.key) || []

          updated.enrichments = updated.enrichments.map((enr) => {
            if (enrichmentKeys.includes(enr.key)) {
              return {
                ...enr,
                status: 'running' as const,
                currentJobId: event.data.jobId,
                lastRunAt: new Date(event.data.startedAt),
              }
            }
            return enr
          })

          // Update summary
          updated.summary = {
            ...updated.summary,
            running: updated.enrichments.filter((e) => e.status === 'running')
              .length,
            ready: updated.enrichments.filter((e) => e.status === 'ready')
              .length,
          }

          return updated
        })
      } else if (
        event.event === 'enrichment:progress' &&
        event.data.sheetId === sheetId &&
        event.data.recordId
      ) {
        window.postMessage({
          type: 'updateBufferedRecord',
          recordId: event.data.recordId,
          values: event.data.values,
        })

        // Update progress for running enrichments
        setStatus((prev) => {
          if (!prev) return prev

          const updated = { ...prev }
          const enrichmentKeys = event.data.enrichments || []

          updated.enrichments = updated.enrichments.map((enr) => {
            if (enrichmentKeys.includes(enr.key)) {
              return {
                ...enr,
                status: 'running' as const,
                recordsProcessed: event.data.progress?.processed,
                totalRecords: event.data.progress?.total,
              }
            }
            return enr
          })

          return updated
        })
      } else if (
        event.event === 'enrichment:completed' &&
        event.data.sheetId === sheetId
      ) {
        // Mark enrichments as completed
        setStatus((prev) => {
          if (!prev) return prev

          const updated = { ...prev }
          const enrichmentKeys = event.data.enrichments || []

          updated.enrichments = updated.enrichments.map((enr) => {
            if (enrichmentKeys.includes(enr.key)) {
              return {
                ...enr,
                status: 'completed' as const,
                recordsProcessed: event.data.recordCount,
                totalRecords: event.data.recordCount,
                currentJobId: null,
              }
            }
            return enr
          })

          // Update summary
          updated.summary = {
            ...updated.summary,
            running: updated.enrichments.filter((e) => e.status === 'running')
              .length,
            completed: updated.enrichments.filter(
              (e) => e.status === 'completed'
            ).length,
          }

          return updated
        })
      } else if (
        event.event === 'enrichment:failed' &&
        event.data.sheetId === sheetId
      ) {
        // Mark enrichments as failed
        setStatus((prev) => {
          if (!prev) return prev

          const updated = { ...prev }
          const enrichmentKeys = event.data.enrichments || []

          updated.enrichments = updated.enrichments.map((enr) => {
            if (enrichmentKeys.includes(enr.key)) {
              return {
                ...enr,
                status: 'failed' as const,
                currentJobId: null,
                error: event.data.error,
              }
            }
            return enr
          })

          // Update summary
          updated.summary = {
            ...updated.summary,
            running: updated.enrichments.filter((e) => e.status === 'running')
              .length,
            failed: updated.enrichments.filter((e) => e.status === 'failed')
              .length,
          }

          return updated
        })
      }
    },
    [sheetId]
  )

  useEffect(() => {
    fetchStatus()
  }, [sheetId])

  return {
    status,
    loading,
    error,
    refetch: fetchStatus,
    hasRunningEnrichments: status?.summary.running
      ? status.summary.running > 0
      : false,
    runningCount: status?.summary.running || 0,
    failedCount: status?.summary.failed || 0,
  }
}
